import React, { useEffect, useRef, useState } from 'react';
import Select from 'react-select';
import DatePicker from 'react-datepicker';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCalendarAlt } from '@fortawesome/free-regular-svg-icons';

import { formatDateAsString } from '../../../../util/StringUtil';
import { hasRoles, EDIT_STUDY_ROLES } from '../../../../util/RoleUtil';

import { deleteStudy, validateStudyProtocolID } from '../../../../services/StudyService';

import { SaveButton } from '../../../common/button/save-button/SaveButton';
import { ToolTip } from '../../../common/tool-tip/ToolTip';
import { Checkbox } from '../../../common/checkbox/Checkbox';

import './StudyTrialEditGeneralTab.css';

export const StudyTrialEditGeneralTab = ({
  study,
  navigateTo,
  context,
  setSpinnerText,
  showToaster,
  toolTipsEnabled,
  editMode,
  handleUpdate,
  provinces,
}) => {
  const [protocolPart1, setProtocolPart1] = useState('');
  const [protocolPart2, setProtocolPart2] = useState('');
  const [protocolPart3, setProtocolPart3] = useState('');
  const [standardProtocolID, setStandardProtocolID] = useState(true);
  const [protocol, setProtocol] = useState('');
  const [originalFacility, setOriginalFacility] = useState('');

  const [studyName, setStudyName] = useState('');

  const studyTypeList = useRef([
    'Clinical Trial',
    'Observational Study',
    'Programmes',
    'Prevention Study',
    'Research Study',
  ]);
  const [selectedType, setSelectedType] = useState(null);

  const [description, setDescription] = useState('');

  const [coEnrolment, setCoEnrolment] = useState(false);
  const [coEnrolmentDisabled, setCoEnrolmentDisabled] = useState(false);

  const [studyDurationPerParticipant, setStudyDurationPerParticipant] = useState('');
  const [studyDurationPerParticipantError, setStudyDurationPerParticipantError] = useState(null);

  const periodList = useRef(['Days', 'Months', 'Years']);
  const [selectedPeriod, setSelectedPeriod] = useState('');

  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [accrualFrom, setAccrualFrom] = useState(null);
  const [accrualTo, setAccrualTo] = useState(null);

  const [dcAlertThreshold, setDcAlertThreshold] = useState('');
  const [sdvAlertThreshold, setSdvAlertThreshold] = useState('');
  const [dcAlertThresholdError, setDcAlertThresholdError] = useState(null);
  const [sdvAlertThresholdError, setSdvAlertThresholdError] = useState(null);

  const [selectedProvince, setSelectedProvince] = useState(null);
  const [selectedDistrict, setSelectedDistrict] = useState(null);
  const [selectedSubDistrict, setSelectedSubDistrict] = useState(null);
  const [selectedFacility, setSelectedFacility] = useState(null);
  const [firstEnrollment, setFirstEnrollment] = useState(null);

  const [districts, setDistricts] = useState([]);
  const [subDistricts, setSubDistricts] = useState([]);
  const [facilities, setFacilities] = useState([]);
  const [facilityExtRef, setFacilityExtRef] = useState('');

  const [protocolError, setProtocolError] = useState('');
  const [studyNameError, setStudyNameError] = useState('');
  const [typeError, setTypeError] = useState('');
  const [provinceError, setProvinceError] = useState('');
  const [districtError, setDistrictError] = useState('');
  const [subDistrictError, setSubDistrictError] = useState('');
  const [facilityError, setFacilityError] = useState('');
  const [startDateError, setStartDateError] = useState('');
  const [endDateError, setEndDateError] = useState('');
  const [acrrualFromDateError, setAcrrualFromDateError] = useState('');
  const [acrrualToDateError, setAcrrualToDateError] = useState('');

  const [screenWidth, setScreenWidth] = useState(window.innerWidth);

  const fieldsDisabled = !hasRoles(context, EDIT_STUDY_ROLES);

  useEffect(() => {
    if (study && Object.keys(study).length !== 0) {
      buildProtocolId(study.protocolID);
      setStudyName(study.studyName || '');
      setSelectedType(study.studyType || null);
      setDescription(study.description || '');
      setCoEnrolment(study.coEnrolmentAllowed || false);
      setStudyDurationPerParticipant(study.studyDurationPerParticipant || '');
      setDcAlertThreshold(study.alertThresholdForDataCapture?.toString() || '');
      setSdvAlertThreshold(study.alertThresholdForSDVCapture?.toString() || '');
      setStartDate(study.startDate ? new Date(study.startDate) : null);
      setEndDate(study.endDate ? new Date(study.endDate) : null);
      setAccrualFrom(study.accrualPeriodFrom ? new Date(study.accrualPeriodFrom) : null);
      setAccrualTo(study.accrualPeriodTo ? new Date(study.accrualPeriodTo) : null);
      setFirstEnrollment(study.firstEnrolmentDate ? new Date(study.firstEnrolmentDate) : null);
      setSelectedProvince(study.province);
      setSelectedDistrict(study.district);
      setSelectedSubDistrict(study.subDistrict);
      setSelectedFacility(study.facility);
      setFacilityExtRef(study.facilityExternalReference);
      setOriginalFacility(study.facilityExternalReference);
      setSelectedPeriod(study.studyDurationPerParticipantMetric || null);

      if (study && study.participants && study.participants.length !== 0) {
        setCoEnrolmentDisabled(true);
      } else {
        setCoEnrolmentDisabled(false);
      }
    }
  }, [study]);

  const updateScreenWidth = () => {
    setScreenWidth(window.innerWidth);
  };

  useEffect(() => {
    window.addEventListener('resize', updateScreenWidth);
    return () => window.removeEventListener('resize', updateScreenWidth);
  });

  const buildProtocolId = (protocolId) => {
    let isStandardProtocolId;
    if (protocolId) {
      isStandardProtocolId = protocolId.startsWith('AUR');
    }

    if (!isStandardProtocolId) {
      setStandardProtocolID(false);
      setProtocol(protocolId);
      return;
    }

    const protocolIdParts = protocolId.split('-');

    const protocolPart1 = protocolIdParts[0].replace('AUR', '');
    const protocolPart2 = protocolIdParts[1];
    const protocolPart3 = protocolIdParts[2];

    setProtocolPart1(protocolPart1);
    setProtocolPart2(protocolPart2);
    setProtocolPart3(protocolPart3);
  };

  const handleStudyTypeChange = (selectedStudyTypeOption) => {
    setSelectedType(selectedStudyTypeOption.value);
  };

  const handleProvinceChange = (selectedProvince) => {
    if (typeof selectedProvince === 'object' && selectedProvince !== null) {
      setSelectedProvince(selectedProvince);
    } else {
      const formattedProvince = provinces.find((province) => province.value.province === selectedProvince);
      setSelectedProvince(formattedProvince);
    }

    const selectedProvinceData = selectedProvince.value;
    const districtOptions = selectedProvinceData.districts.map((district) => ({
      value: district,
      label: district.district,
    }));
    setDistricts(districtOptions);
  };

  const handleDistrictChange = (selectedDistrict) => {
    if (typeof selectedDistrict === 'object' && selectedDistrict !== null) {
      setSelectedDistrict(selectedDistrict);
    } else {
      const formattedDistrict = districts.find((district) => district.value.district === selectedDistrict);
      setSelectedDistrict(formattedDistrict);
    }

    const selectedDistrictData = selectedDistrict.value;
    const subDistrictOptions = selectedDistrictData.subDistricts.map((subDistrict) => ({
      value: subDistrict,
      label: subDistrict.subDistrict,
    }));
    setSubDistricts(subDistrictOptions);
  };

  const handleSubDistrictChange = (selectedSubDistrict) => {
    if (typeof selectedSubDistrict === 'object' && selectedSubDistrict !== null) {
      setSelectedSubDistrict(selectedSubDistrict);
    } else {
      const formattedSubDistrict = subDistricts.find(
        (subDistrict) => subDistrict.value.subDistrict === selectedSubDistrict
      );
      setSelectedSubDistrict(formattedSubDistrict);
    }

    const selectedSubDistrictData = selectedSubDistrict.value;
    const facilityOptions = selectedSubDistrictData.facilities.map((facility) => ({
      value: facility,
      label: facility.facility,
    }));
    setFacilities(facilityOptions);
  };

  const handleFacilityChange = (selectedFacility) => {
    setFacilityExtRef(selectedFacility.value.externalReference);
    if (typeof selectedFacility === 'object' && selectedFacility !== null) {
      setSelectedFacility(selectedFacility);
    } else {
      const formattedFacility = facilities.find((facility) => facility.value.facility === selectedFacility);
      setSelectedFacility(formattedFacility);
    }
  };

  const handleYesChange = () => {
    setCoEnrolment(true);
  };

  const handleNoChange = () => {
    setCoEnrolment(false);
  };

  const handlePeriodChange = (selectedOption) => {
    setSelectedPeriod(selectedOption.value);
  };

  const validateProtocolID = async () => {
    const protocolIdToValidate = 'AUR' + protocolPart1 + '-' + protocolPart2 + '-' + protocolPart3;
    setSpinnerText('Validating Protocol ID');
    const [data, error] = await validateStudyProtocolID(context.username, protocolIdToValidate);
    if (data) {
      if (data.responseStatus === 'Success') {
        if (data.protocolIDAlreadyExists) {
          if (protocolIdToValidate === study.protocolID) {
            setProtocolError('');
          } else {
            setProtocolError('Protocol ID already exists.');
          }
        } else {
          setProtocolError('');
        }
      } else {
        showToaster(data);
      }
    } else {
      showToaster({
        responseStatus: 'Error',
        responseMessage: error.message,
      });
    }

    setSpinnerText(null);
  };

  const validateRequiredFields = () => {
    let isValid = true;
    if (standardProtocolID) {
      if (!protocolPart1 || !protocolPart2 || !protocolPart3) {
        setProtocolError('*REQUIRED');
        isValid = false;
      } else {
        validateProtocolID();
      }
    } else {
      if (!protocol) {
        setProtocolError('*REQUIRED');
        isValid = false;
      } else {
        setProtocolError('');
      }
    }

    if (!studyName) {
      setStudyNameError('*REQUIRED');
      isValid = false;
    } else {
      setStudyNameError('');
    }

    if (!selectedType) {
      setTypeError('*REQUIRED');
      isValid = false;
    } else {
      setTypeError('');
    }

    if (!startDate) {
      setStartDateError('*REQUIRED');
      isValid = false;
    } else {
      setStartDateError('');
    }

    if (!endDate) {
      setEndDateError('*REQUIRED');
      isValid = false;
    } else {
      setEndDateError('');
    }

    if (!accrualFrom) {
      setAcrrualFromDateError('*REQUIRED');
      isValid = false;
    } else {
      setAcrrualFromDateError('');
    }

    if (!selectedProvince) {
      setProvinceError('*REQUIRED');
      isValid = false;
    } else {
      setProvinceError('');
    }

    if (!selectedDistrict) {
      setDistrictError('*REQUIRED');
      isValid = false;
    } else {
      setDistrictError('');
    }

    if (!selectedSubDistrict) {
      setSubDistrictError('*REQUIRED');
      isValid = false;
    } else {
      setSubDistrictError('');
    }

    if (!selectedFacility) {
      setFacilityError('*REQUIRED');
      isValid = false;
    } else {
      setFacilityError('');
    }

    if (!accrualTo) {
      setAcrrualToDateError('*REQUIRED');
      isValid = false;
    } else {
      setAcrrualToDateError('');
    }

    if (startDate && endDate) {
      if (endDate <= startDate) {
        setStartDateError('*End Date cannot be before Start Date');
        setEndDateError('*End Date cannot be before Start Date');
        isValid = false;
      } else {
        setStartDateError('');
        setEndDateError('');
      }
    } else {
      setStartDateError('');
      setEndDateError('');
    }

    if (studyDurationPerParticipant < 0) {
      setStudyDurationPerParticipantError('*Value Cannot be less than 0.');
    } else {
      setStudyDurationPerParticipantError('');
    }

    if (dcAlertThreshold < 0) {
      setDcAlertThresholdError('*Value Cannot be less than 0.');
    } else {
      setDcAlertThresholdError('');
    }

    if (sdvAlertThreshold < 0) {
      setSdvAlertThresholdError('*Value Cannot be less than 0.');
    } else {
      setSdvAlertThresholdError('');
    }

    return isValid;
  };

  const handleSaveChangesClick = () => {
    const validated = validateRequiredFields();

    if (!validated) {
      return;
    }

    let studyRoles = [];

    study?.siteStaff.forEach((staff) => {
      let roles = staff.studyRole.split(', ');
      roles.forEach((role) => {
        studyRoles.push({
          roleName: role,
          personExternalReferences: [staff.personExternalReference],
        });
      });
    });

    const updateStudyStudyScheduleVOS = (study?.visitSchedules || []).map((schedule) => ({
      name: schedule.name || '',
      studyScheduleExternalReference: schedule.studyScheduleExternalReference,
      updateStudyVisitScheduleVOS: (schedule.visitPacks || []).map((visit) => ({
        enrolmentVisit: !!visit.enrolmentVisit,
        day: Number(visit.day) || 0,

        formExternalReferences: (visit.forms || []).map((form) => form.formExternalReference),

        longVisit: !!visit.longVisit,
        visitCode: visit.visitCode,

        ...(visit.visitCodeEdited && visit.visitCodeToUpdate ? { visitCodeToUpdate: visit.visitCodeToUpdate } : {}),

        visitType: visit.visitType,
        windowAfter: Number(visit.windowAfter) || 0,
        windowBefore: Number(visit.windowBefore) || 0,
      })),
    }));

    let protocolIdToSave = '';
    if (standardProtocolID) {
      protocolIdToSave = 'AUR' + protocolPart1 + '-' + protocolPart2 + '-' + protocolPart3;
    } else {
      protocolIdToSave = protocol;
    }
    const newStudy = {
      ...study,
      roles: studyRoles,
      protocolID: protocolIdToSave,
      studyName,
      studyType: selectedType,
      description,
      coEnrolmentAllowed: coEnrolment,
      studyDurationPerParticipant,
      studyDurationPerParticipantMetric: selectedPeriod,
      alertThresholdForDataCapture: dcAlertThreshold,
      alertThresholdForSDVCapture: sdvAlertThreshold,
      facilityExternalReference: originalFacility,
      facilityExternalReferenceToUpdate: facilityExtRef,
      accrualPeriodFrom: formatDateAsString(accrualFrom) || '',
      accrualPeriodTo: formatDateAsString(accrualTo) || '',
      endDate: formatDateAsString(endDate) || '',
      firstEnrolmentDate: formatDateAsString(firstEnrollment) || '',
      startDate: formatDateAsString(startDate) || '',
      updateStudyStudyScheduleVOS,
    };

    handleUpdate(newStudy);
  };

  const handleDeleteStudyClick = async () => {
    setSpinnerText('Deleting Study');
    const [data, error] = await deleteStudy(
      context.username,
      context.additionalContext.facilityExternalReference,
      context.additionalContext.studyExternalReference
    );
    if (data) {
      if (data.responseStatus === 'Success') {
        navigateTo('studyTrialAdmin');
      } else {
        showToaster(data);
      }
    } else {
      showToaster({
        responseStatus: 'Error',
        responseMessage: error.message,
      });
    }
    setSpinnerText(null);
  };

  const renderLeftInputs = () => {
    return (
      <div className="form-section-left" style={{ paddingTop: '0' }}>
        {standardProtocolID && (
          <div className="input-container">
            <label className={'input-label'}>Protocol ID:</label>
            <label className={'study-trial-protocol-label'}>AUR</label>
            <input
              className={`text-input-small ${protocolError && 'error-border'}`}
              type="text"
              value={protocolPart1}
              disabled
              onChange={({ target }) => {
                setProtocolPart1(target.value);
              }}
            />
            <label className={'study-trial-protocol-label'}>-</label>
            <input
              className={`text-input-small ${protocolError && 'error-border'}`}
              type="text"
              value={protocolPart2}
              disabled
              onChange={({ target }) => {
                setProtocolPart2(target.value);
              }}
            />
            <label className={'study-trial-protocol-label'}>-</label>
            <input
              className={`text-input-small ${protocolError && 'error-border'}`}
              type="text"
              value={protocolPart3}
              disabled
              onChange={({ target }) => {
                setProtocolPart3(target.value);
              }}
            />
            {toolTipsEnabled && (
              <ToolTip
                tooltipExternalReference={'protocolID123'}
                context={context}
                setSpinnerText={setSpinnerText}
                showToaster={showToaster}
                fieldName={'Protocol ID:'}
              />
            )}
          </div>
        )}
        <div className="protocol-error-message">
          {protocolError && <div className="small-red-text">{protocolError}</div>}
        </div>

        {!standardProtocolID && (
          <div className="input-container">
            <label className={'input-label'}>Protocol ID:</label>
            <input
              className="text-input"
              type="text"
              value={protocol}
              disabled
              onChange={({ target }) => {
                setProtocol(target.value);
              }}
            />
            {toolTipsEnabled && (
              <ToolTip
                tooltipExternalReference={'protocolID123'}
                context={context}
                setSpinnerText={setSpinnerText}
                showToaster={showToaster}
                fieldName={'Protocol ID:'}
              />
            )}
          </div>
        )}
        <div className="input-container">
          <label className={'input-label'}>Study Name:</label>
          <input
            className={`text-input ${studyNameError && 'error-border'}`}
            type="text"
            value={studyName}
            disabled
            onChange={({ target }) => {
              setStudyName(target.value);
            }}
          />
          {toolTipsEnabled && (
            <ToolTip
              tooltipExternalReference={'studyName123'}
              context={context}
              setSpinnerText={setSpinnerText}
              showToaster={showToaster}
              fieldName={'Study Name:'}
            />
          )}
        </div>
        <div className="protocol-error-message">
          {studyNameError && <div className="small-red-text">{studyNameError}</div>}
        </div>
        <div className="input-container">
          <label className={'input-label'}>Type:</label>
          <Select
            options={studyTypeList.current.map((studyType, index) => ({
              value: studyType,
              label: studyType,
            }))}
            closeMenuOnSelect={true}
            onChange={handleStudyTypeChange}
            value={selectedType ? { value: selectedType, label: selectedType } : null}
            isDisabled={fieldsDisabled}
            classNamePrefix={`input-select${typeError ? 'error-border' : ''}`}
          />
          {toolTipsEnabled && (
            <ToolTip
              tooltipExternalReference={'studyType123'}
              context={context}
              setSpinnerText={setSpinnerText}
              showToaster={showToaster}
              fieldName={'Types:'}
            />
          )}
        </div>
        <div className="protocol-error-message">{typeError && <div className="small-red-text">{typeError}</div>}</div>
        <div className="input-container">
          <label className={'input-label'}>Site Activation Date:</label>
          <DatePicker
            selected={startDate}
            className={`text-input ${startDateError && 'error-border'}`}
            dateFormat="dd-MM-yyyy"
            showPopperArrow={false}
            showYearDropdown
            showMonthDropdown
            dropdownMode="select"
            placeholderText="Select..."
            disabled={fieldsDisabled}
            onChange={(selectedDate) => setStartDate(selectedDate)}
          />
          <FontAwesomeIcon icon={faCalendarAlt} className="calendar-icon" />
          {toolTipsEnabled && (
            <ToolTip
              tooltipExternalReference={'studyStartDate123'}
              context={context}
              setSpinnerText={setSpinnerText}
              showToaster={showToaster}
              fieldName={'Site Activation Date:'}
            />
          )}
        </div>
        <div className="error-message">{startDateError && <div className="small-red-text">{startDateError}</div>}</div>
        <div className="input-container">
          <label className={'input-label'}>End Date:</label>
          <DatePicker
            selected={endDate}
            className={`text-input ${endDateError && 'error-border'}`}
            dateFormat="dd-MM-yyyy"
            showPopperArrow={false}
            showYearDropdown
            showMonthDropdown
            dropdownMode="select"
            placeholderText="Select..."
            disabled={fieldsDisabled}
            onChange={(selectedDate) => setEndDate(selectedDate)}
          />
          <FontAwesomeIcon icon={faCalendarAlt} className="calendar-icon" />
          {toolTipsEnabled && (
            <ToolTip
              tooltipExternalReference={'studyEndDate123'}
              context={context}
              setSpinnerText={setSpinnerText}
              showToaster={showToaster}
              fieldName={'Registration Date:'}
            />
          )}
        </div>
        <div className="error-message">{endDateError && <div className="small-red-text">{endDateError}</div>}</div>
        <div className="input-container">
          <label className={'input-label'}>Accrual Period From:</label>
          <DatePicker
            selected={accrualFrom}
            className={`text-input ${acrrualFromDateError && 'error-border'}`}
            dateFormat="dd-MM-yyyy"
            showPopperArrow={false}
            showYearDropdown
            showMonthDropdown
            dropdownMode="select"
            placeholderText="Select..."
            disabled={fieldsDisabled}
            onChange={(selectedDate) => setAccrualFrom(selectedDate)}
          />
          <FontAwesomeIcon icon={faCalendarAlt} className="calendar-icon" />
          {toolTipsEnabled && (
            <ToolTip
              tooltipExternalReference={'siteAccrualFrom123'}
              context={context}
              setSpinnerText={setSpinnerText}
              showToaster={showToaster}
              fieldName={'Accrual Period From:'}
            />
          )}
        </div>
        <div className="error-message">
          {acrrualFromDateError && <div className="small-red-text">{acrrualFromDateError}</div>}
        </div>
      </div>
    );
  };

  const renderLeftBottomInputs = () => {
    return (
      <div className="form-section-left" style={{ paddingTop: '0' }}>
        <div className="input-container">
          <label className={'input-label'}>Co-enrolment Allowed:</label>
          <Checkbox
            onChange={handleYesChange}
            label={'Yes'}
            checked={coEnrolment}
            fillStyle={true}
            color={'#000000'}
            disabled={coEnrolmentDisabled || fieldsDisabled}
          />
          <Checkbox
            onChange={handleNoChange}
            label={'No'}
            checked={!coEnrolment}
            fillStyle={true}
            color={'#000000'}
            disabled={coEnrolmentDisabled || fieldsDisabled}
          />
          {toolTipsEnabled && (
            <ToolTip
              tooltipExternalReference={'studycoEnrolment123'}
              context={context}
              setSpinnerText={setSpinnerText}
              showToaster={showToaster}
              fieldName={'Co-enrollment allowed:'}
            />
          )}
        </div>
        <div className="input-container">
          <label className={'input-label'}>Alert Threshold for Data Capture:</label>
          <input
            className={`text-input-small ${dcAlertThresholdError && 'error-border'}`}
            type="number"
            value={dcAlertThreshold}
            disabled={fieldsDisabled}
            onChange={({ target }) => {
              setDcAlertThreshold(target.value);
            }}
          />
          <label className={'input-label-back'}> days after Visit Date.</label>
          {toolTipsEnabled && (
            <ToolTip
              tooltipExternalReference={'dataCaptureAlertThreshold123'}
              context={context}
              setSpinnerText={setSpinnerText}
              showToaster={showToaster}
              fieldName={'Alert Threshold for Data Capture:'}
            />
          )}
        </div>
        <div className="studies-error-message">
          {dcAlertThresholdError && <div className="small-red-text">{dcAlertThresholdError}</div>}
        </div>

        <div className="input-container">
          <label className={'input-label'}>Alert Threshold for SDV Capture:</label>
          <input
            className={`text-input-small ${sdvAlertThresholdError && 'error-border'}`}
            type="number"
            value={sdvAlertThreshold}
            disabled={fieldsDisabled}
            onChange={({ target }) => {
              setSdvAlertThreshold(target.value);
            }}
          />
          <label className={'input-label-back'}> days after Data Capture Date.</label>
          {toolTipsEnabled && (
            <ToolTip
              tooltipExternalReference={'sdvCaptureAlertThreshold123'}
              context={context}
              setSpinnerText={setSpinnerText}
              showToaster={showToaster}
              fieldName={'Alert Threshold for SDV Capture:'}
            />
          )}
        </div>
        <div className="studies-error-message">
          {sdvAlertThresholdError && <div className="small-red-text">{sdvAlertThresholdError}</div>}
        </div>
      </div>
    );
  };

  const renderAccrualPeriodTo = () => (
    <>
      <div className="input-container">
        <label className={'input-label-right'}>Accrual Period To:</label>
        <DatePicker
          selected={accrualTo}
          className={`text-input ${acrrualToDateError && 'error-border'}`}
          dateFormat="dd/MM/yyy"
          showPopperArrow={false}
          showYearDropdown
          showMonthDropdown
          dropdownMode="select"
          placeholderText="Select..."
          disabled={fieldsDisabled}
          onChange={(selectedDate) => setAccrualTo(selectedDate)}
        />
        <FontAwesomeIcon icon={faCalendarAlt} className="calendar-icon-right" />
        {toolTipsEnabled && (
          <ToolTip
            tooltipExternalReference={'siteAccrualTo123'}
            context={context}
            setSpinnerText={setSpinnerText}
            showToaster={showToaster}
            fieldName={'Accrual Period To:'}
          />
        )}
      </div>
      <div className="error-message">
        {acrrualToDateError && <div className="small-red-text">{acrrualToDateError}</div>}
      </div>
    </>
  );

  const renderRightInputs = () => {
    return (
      <div className="form-section-right">
        {screenWidth <= 1300 && renderAccrualPeriodTo()}

        <div className="input-container">
          <label className={'input-label-right'}>Province:</label>
          <Select
            options={provinces}
            closeMenuOnSelect={true}
            onChange={handleProvinceChange}
            isDisabled={fieldsDisabled}
            value={
              selectedProvince && typeof selectedProvince === 'object'
                ? selectedProvince
                : { value: selectedProvince, label: selectedProvince }
            }
            classNamePrefix={`input-select${provinceError ? 'error-border' : ''}`}
          />
          {toolTipsEnabled && (
            <ToolTip
              tooltipExternalReference={'siteProvince123'}
              context={context}
              setSpinnerText={setSpinnerText}
              showToaster={showToaster}
              fieldName={'Province:'}
            />
          )}
        </div>
        <div className="error-message-right">
          {provinceError && <div className="small-red-text">{provinceError}</div>}
        </div>
        <div className="input-container">
          <label className={'input-label-right'}>District:</label>
          <Select
            options={districts}
            closeMenuOnSelect={true}
            onChange={handleDistrictChange}
            isDisabled={fieldsDisabled}
            value={
              selectedDistrict && typeof selectedDistrict === 'object'
                ? selectedDistrict
                : { value: selectedDistrict, label: selectedDistrict }
            }
            classNamePrefix={`input-select${districtError ? 'error-border' : ''}`}
          />
          {toolTipsEnabled && (
            <ToolTip
              tooltipExternalReference={'siteDistrict123'}
              context={context}
              setSpinnerText={setSpinnerText}
              showToaster={showToaster}
              fieldName={'District:'}
            />
          )}
        </div>
        <div className="error-message-right">
          {districtError && <div className="small-red-text">{districtError}</div>}
        </div>
        <div className="input-container">
          <label className={'input-label-right'}>Sub-District:</label>
          <Select
            options={subDistricts}
            closeMenuOnSelect={true}
            onChange={handleSubDistrictChange}
            isDisabled={fieldsDisabled}
            value={
              selectedSubDistrict && typeof selectedSubDistrict === 'object'
                ? selectedSubDistrict
                : { value: selectedSubDistrict, label: selectedSubDistrict }
            }
            classNamePrefix={`input-select${subDistrictError ? 'error-border' : ''}`}
          />
          {toolTipsEnabled && (
            <ToolTip
              tooltipExternalReference={'siteSubDistrict123'}
              context={context}
              setSpinnerText={setSpinnerText}
              showToaster={showToaster}
              fieldName={'Sub-District:'}
            />
          )}
        </div>
        <div className="error-message-right">
          {subDistrictError && <div className="small-red-text">{subDistrictError}</div>}
        </div>
        <div className="input-container">
          <label className={'input-label-right'}>Facility:</label>
          <Select
            options={facilities}
            closeMenuOnSelect={true}
            onChange={handleFacilityChange}
            isDisabled={fieldsDisabled}
            value={
              selectedFacility && typeof selectedFacility === 'object'
                ? selectedFacility
                : { value: selectedFacility, label: selectedFacility }
            }
            classNamePrefix={`input-select${facilityError ? 'error-border' : ''}`}
          />
          {toolTipsEnabled && (
            <ToolTip
              tooltipExternalReference={'siteFacilities123'}
              context={context}
              setSpinnerText={setSpinnerText}
              showToaster={showToaster}
              fieldName={'Facility:'}
            />
          )}
        </div>
        <div className="error-message-right">
          {facilityError && <div className="small-red-text">{facilityError}</div>}
        </div>

        {screenWidth > 1300 && renderAccrualPeriodTo()}
      </div>
    );
  };

  const renderRightBottomInputs = () => {
    return (
      <div className="form-section-right">
        <div className="input-container">
          <label className={'input-label'}>Study Duration Per Participant:</label>
          <input
            className={`text-input-small ${studyDurationPerParticipantError && 'error-border'}`}
            style={{ marginRight: 10 }}
            type="number"
            value={studyDurationPerParticipant}
            disabled={fieldsDisabled}
            onChange={({ target }) => {
              setStudyDurationPerParticipant(target.value);
            }}
          />
          <Select
            options={periodList.current.map((period) => ({
              value: period,
              label: period,
            }))}
            closeMenuOnSelect={true}
            onChange={handlePeriodChange}
            isDisabled={fieldsDisabled}
            value={selectedPeriod ? { value: selectedPeriod, label: selectedPeriod } : null}
            classNamePrefix="input-select"
          />
          {toolTipsEnabled && (
            <ToolTip
              tooltipExternalReference={'studyDurationPerParticipant123'}
              context={context}
              setSpinnerText={setSpinnerText}
              showToaster={showToaster}
              fieldName={'Study Duration Per Participant:'}
            />
          )}
        </div>
        <div className="studies-error-message">
          {studyDurationPerParticipantError && <div className="small-red-text">{studyDurationPerParticipantError}</div>}
        </div>
      </div>
    );
  };

  return (
    <div className="study-trial-edit-general-tab">
      <div>
        <div className="study-trial-edit-general-tab-inputs">
          {renderLeftInputs()}
          {renderRightInputs()}
        </div>
        <div className="input-container">
          <label className={'input-area-label'}>Description:</label>
          <textarea
            className="input-area"
            spellCheck={true}
            type="text"
            value={description}
            disabled={fieldsDisabled}
            onChange={({ target }) => {
              setDescription(target.value);
            }}
          />
          {toolTipsEnabled && (
            <ToolTip
              tooltipExternalReference={'studyDescription123'}
              context={context}
              setSpinnerText={setSpinnerText}
              showToaster={showToaster}
              fieldName={'Description:'}
            />
          )}
        </div>
        <div className="study-trial-edit-general-tab-inputs">
          {renderLeftBottomInputs()}
          {renderRightBottomInputs()}
        </div>
      </div>
      {editMode && (
        <div className={'study-button-container'}>
          {study?.canDeleteStudy && (
            <SaveButton
              color={'#ffffff'}
              border={'1px solid #077187'}
              textColor={'#077187'}
              title={'Delete Study'}
              disabled={fieldsDisabled}
              onClick={handleDeleteStudyClick}
            />
          )}
          <SaveButton
            color={'#077187'}
            title={'Save Changes'}
            disabled={fieldsDisabled}
            onClick={handleSaveChangesClick}
          />
        </div>
      )}
    </div>
  );
};
